import React from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Plus, Upload } from "lucide-react";
import { AIDescriberButton } from "./article_form/AIDescriberButton";
import { ExistingImagesGallery } from "./ExistingImagesGallery";
import { NewPhotosPreview } from "./NewPhotosPreview";
import { AISuggestEanButton } from "./article_form/AISuggestEanButton";
import { EbayAspectsHelperButton } from "./article_form/EbayAspectsHelperButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/**
 * Article create/edit form — extracted from AdminApp.jsx during refactor.
 * Wraps the full Dialog: trigger button + modal content.
 *
 * Parent must manage all form state and submit/file/select/reset handlers.
 */
export const ArticleFormDialog = ({
  isOpen,
  setIsOpen,
  editingArticle,
  formData,
  ivaOptions,
  selectedFile,
  uploading,
  newsletterSubscriptions,
  onSubmit,
  onInputChange,
  onSelectChange,
  onFileChange,
  onFilesReplaced,
  onImagesReordered,
  resetForm,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          resetForm();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700" data-testid="article-form-trigger">
          <Plus className="w-4 h-4 mr-2" />
          {editingArticle ? "Modifica Articolo" : "Nuovo Articolo"}
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100%-1rem)] max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="article-form-dialog">
        <DialogHeader>
          <DialogTitle>{editingArticle ? "Modifica Articolo" : "Crea Nuovo Articolo"}</DialogTitle>
          <DialogDescription>
            {editingArticle
              ? "Modifica i dettagli dell'articolo selezionato"
              : "Compila i campi per creare un nuovo articolo"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Informazioni Base
              </h3>

              <div className="space-y-2">
                <Label htmlFor="sku">SKU Prodotto *</Label>
                <Input
                  id="sku"
                  data-testid="article-sku-input"
                  name="sku"
                  value={formData.sku}
                  onChange={onInputChange}
                  placeholder="es. EDC001"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Titolo *</Label>
                <Input
                  id="title"
                  data-testid="article-title-input"
                  name="title"
                  value={formData.title}
                  onChange={onInputChange}
                  placeholder="Nome del prodotto"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrizione *</Label>
                <Textarea
                  id="description"
                  data-testid="article-description-input"
                  name="description"
                  value={formData.description}
                  onChange={onInputChange}
                  placeholder="Descrizione dettagliata del prodotto..."
                  rows={4}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Input
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={onInputChange}
                  placeholder="es. Giornali, Riviste, Accessori"
                />
              </div>
            </div>

            {/* Pricing & Stock */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Prezzo e Magazzino
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Prezzo (€) *</Label>
                  <Input
                    id="price"
                    data-testid="article-price-input"
                    name="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={onInputChange}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="iva_percentage">IVA *</Label>
                  <Select value={formData.iva_percentage} onValueChange={onSelectChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona IVA" />
                    </SelectTrigger>
                    <SelectContent>
                      {ivaOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity_stock">Quantità in Magazzino *</Label>
                <Input
                  id="quantity_stock"
                  data-testid="article-stock-input"
                  name="quantity_stock"
                  type="number"
                  min="0"
                  value={formData.quantity_stock}
                  onChange={onInputChange}
                  placeholder="0"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="shipping_cost_national">Spedizione Nazionale (€)</Label>
                  <Input
                    id="shipping_cost_national"
                    name="shipping_cost_national"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.shipping_cost_national}
                    onChange={onInputChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shipping_cost_international">Spedizione Estera (€)</Label>
                  <Input
                    id="shipping_cost_international"
                    name="shipping_cost_international"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.shipping_cost_international}
                    onChange={onInputChange}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="weight">Peso (kg)</Label>
                  <Input
                    id="weight"
                    name="weight"
                    type="number"
                    step="0.001"
                    min="0"
                    value={formData.weight}
                    onChange={onInputChange}
                    placeholder="0.000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dimensions">Dimensioni (LxWxH cm)</Label>
                  <Input
                    id="dimensions"
                    name="dimensions"
                    value={formData.dimensions}
                    onChange={onInputChange}
                    placeholder="es. 20x15x5"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Availability Type, Preorder, Reservation */}
          <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-gray-900">Disponibilità Prodotto</h3>

            <div className="space-y-2">
              <Label htmlFor="availability_type">Tipo Disponibilità</Label>
              <select
                id="availability_type"
                name="availability_type"
                value={formData.availability_type || "available"}
                onChange={onInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="available">✅ Disponibile</option>
                <option value="preorder">🚀 Prevendita</option>
                <option value="reservation">📋 Prenotazione</option>
              </select>
            </div>

            {formData.availability_type === "preorder" && (
              <div className="space-y-2">
                <Label htmlFor="preorder_date">
                  Data Disponibilità <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="preorder_date"
                  name="preorder_date"
                  type="date"
                  value={formData.preorder_date || ""}
                  onChange={onInputChange}
                  data-testid="preorder-date-input"
                />
                {!formData.preorder_date ? (
                  <div
                    className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-sm"
                    data-testid="preorder-date-warning"
                  >
                    <span className="text-lg leading-none">⚠️</span>
                    <div>
                      <p className="font-semibold">Data prevista obbligatoria per Google Shopping</p>
                      <p className="text-xs mt-0.5">
                        Senza data, Google Merchant Center rifiuterà il prodotto.
                        Inserisci la data prevista di arrivo o, se non la conosci,
                        verrà usato un fallback di +30 giorni dalla data di oggi.
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    Data prevista per la disponibilità del prodotto (formato YYYY-MM-DD)
                  </p>
                )}
              </div>
            )}

            {formData.availability_type === "reservation" && (
              <div className="space-y-2">
                <Label htmlFor="reservation_info">Info Prenotazione</Label>
                <textarea
                  id="reservation_info"
                  name="reservation_info"
                  value={formData.reservation_info || ""}
                  onChange={onInputChange}
                  placeholder="es. Prenotazione gratuita. Ti avviseremo quando sarà disponibile."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          {/* Photo Upload */}
          <div className="space-y-2">
            <Label htmlFor="photo">Foto Prodotto (fino a 5)</Label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <Label htmlFor="photo" className="cursor-pointer">
                <span className="text-sm text-gray-600">
                  Clicca per selezionare 1-5 foto o trascina qui
                </span>
                <Input
                  id="photo"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onFileChange}
                  className="hidden"
                />
              </Label>
              {selectedFile && (
                <NewPhotosPreview
                  files={selectedFile}
                  onChange={(next) => onFilesReplaced && onFilesReplaced(next.length ? next : null)}
                />
              )}
              {editingArticle &&
                (editingArticle.images_url?.[0] || editingArticle.photo_url) &&
                !selectedFile && (
                  <p className="mt-2 text-sm text-blue-600">Foto attuale caricata</p>
                )}
            </div>

            {/* Gallery: existing images with reorder / set-primary / delete */}
            {editingArticle && (editingArticle.images_url || []).length > 0 && !selectedFile && (
              <ExistingImagesGallery
                articleId={editingArticle.id}
                images={editingArticle.images_url}
                onUpdated={(newOrder) => {
                  // Bubble the new order up so the parent state reflects immediately.
                  if (typeof onImagesReordered === "function") {
                    onImagesReordered(newOrder);
                  }
                }}
              />
            )}
            {/* AI Describer button */}
            <AIDescriberButton
              selectedFile={selectedFile}
              existingImageUrl={
                editingArticle?.images_url?.[0] ||
                (editingArticle?.photo_url
                  ? `${process.env.REACT_APP_BACKEND_URL}${editingArticle.photo_url}`
                  : null)
              }
            />
          </div>

          {/* Newsletter Toggle - Only for new articles */}
          {!editingArticle && formData.publication_channel !== "ebay" && (
            <div className="space-y-2 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="sendNewsletter"
                  data-testid="article-newsletter-checkbox"
                  defaultChecked={true}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <label
                    htmlFor="sendNewsletter"
                    className="text-sm font-medium text-gray-900 cursor-pointer"
                  >
                    📧 Invia newsletter automatica agli iscritti
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Quando salvi, verrà inviata una email a tutti gli iscritti (
                    {newsletterSubscriptions.length} persone) per notificare il nuovo prodotto
                  </p>
                </div>
              </div>
            </div>
          )}
          {!editingArticle && formData.publication_channel === "ebay" && (
            <p className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="newsletter-not-sent-ebay-only">La newsletter non verrà inviata: l’articolo è impostato su Solo eBay.</p>
          )}

          {/* eBay Publish Section - Always visible (new + editing) so admin can
              set/fix category and condition before or after first publish */}
          {(() => {
            const isNew = !editingArticle;
            const hasEbayListing = editingArticle?.ebay?.offer_id;
            const hasEbayError = editingArticle?.ebay?.status === "error";
            return (
            <div className="space-y-3 bg-amber-50 p-4 rounded-lg border border-amber-200" data-testid="ebay-publish-section">
              {isNew ? (
                <div className="space-y-2" data-testid="publication-channel-selector">
                  <p className="text-sm font-medium text-gray-900">Dove vuoi pubblicare questo articolo?</p>
                  {[['both', 'Sito + eBay', 'Pubblica sul catalogo e sul marketplace eBay (impostazione attuale).'], ['website', 'Solo sito', 'Visibile nel catalogo e checkout del sito; nessuna pubblicazione eBay.'], ['ebay', 'Solo eBay', 'Resta nel magazzino e viene pubblicato su eBay, senza comparire sul sito.']].map(([value, label, help]) => (
                    <label key={value} className="flex items-start gap-3 rounded border border-amber-200 bg-white p-3 cursor-pointer">
                      <input type="radio" name="publication_channel" value={value} checked={(formData.publication_channel || 'both') === value} onChange={onInputChange} data-testid={`publication-channel-${value}`} />
                      <span><span className="block text-sm font-medium text-gray-900">{label}</span><span className="block text-xs text-gray-600 mt-1">{help}</span></span>
                    </label>
                  ))}
                </div>
              ) : (
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    🟦 Impostazioni eBay{" "}
                    {hasEbayError && <span className="text-red-600">⚠️ Errore precedente</span>}
                    {hasEbayListing && !hasEbayError && (
                      <span className="text-green-600 text-xs ml-1">✓ Pubblicato (Offer {editingArticle.ebay.offer_id})</span>
                    )}
                    {!hasEbayListing && !hasEbayError && (
                      <span className="text-gray-500 text-xs ml-1">Non ancora pubblicato</span>
                    )}
                  </h4>
                  {hasEbayError && editingArticle?.ebay?.last_error && (
                    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">
                      <b>Errore eBay:</b> {editingArticle.ebay.last_error}
                    </p>
                  )}
                  <p className="text-xs text-gray-600 mt-1">
                    Imposta categoria/condizione, salva, poi clicca <b>"Pubblica eBay"</b> dalla griglia per sincronizzare.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-7">
                <div>
                  <Label htmlFor="ebay_category_id" className="text-xs text-gray-700">
                    Categoria eBay (categoryId) — Marketplace IT
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="ebay_category_id"
                      name="ebay_category_id"
                      placeholder="es. 180273 (Modellini auto)"
                      defaultValue={editingArticle?.ebay_category_id ?? ""}
                      className="h-8 text-sm flex-1"
                      data-testid="ebay-category-id-input"
                    />
                    <button
                      type="button"
                      className="h-8 px-2 text-xs bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded whitespace-nowrap"
                      onClick={async () => {
                        const title = document.getElementById('title')?.value?.trim();
                        if (!title) {
                          alert("Inserisci prima un titolo per il prodotto");
                          return;
                        }
                        try {
                          const token = sessionStorage.getItem("access_token");
                          const url = `${API}/ebay/suggest-category?q=${encodeURIComponent(title)}`;
                          const resp = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
                          const suggestions = resp.data?.suggestions || [];
                          if (suggestions.length === 0) {
                            alert("Nessun suggerimento trovato. Prova a riformulare il titolo.");
                            return;
                          }
                          const choices = suggestions.slice(0, 5).map((s, i) => `${i + 1}. [${s.category_id}] ${s.path}`).join("\n");
                          const pick = window.prompt(`Categorie eBay IT suggerite per:\n"${title}"\n\n${choices}\n\nDigita il numero (1-${Math.min(5, suggestions.length)}) o premi Annulla:`, "1");
                          const idx = parseInt(pick, 10) - 1;
                          if (idx >= 0 && idx < suggestions.length) {
                            document.getElementById('ebay_category_id').value = suggestions[idx].category_id;
                          }
                        } catch (err) {
                          alert(`Errore suggerimento: ${err.response?.data?.detail || err.message}`);
                        }
                      }}
                      data-testid="ebay-category-suggest-btn"
                      title="Trova la categoria eBay GIUSTA per Marketplace IT dato il titolo del prodotto"
                    >
                      🔍 Suggerisci
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    Cliccando <b>🔍 Suggerisci</b> ottieni le categorie corrette eBay IT in base al titolo. Manuali: Modellini auto/camion <b>180273</b> · Pokemon box <b>261044</b> · Pokemon pacchetti <b>183456</b> · Formula 1 <b>2876</b>
                  </p>
                </div>
                <div>
                  <Label htmlFor="ebay_condition" className="text-xs text-gray-700">
                    Condizione articolo
                  </Label>
                  <select
                    id="ebay_condition"
                    name="ebay_condition"
                    defaultValue={editingArticle?.ebay_condition ?? ""}
                    className="h-8 text-sm w-full border border-gray-300 rounded px-2"
                    data-testid="ebay-condition-select"
                  >
                    <option value="">Default (.env)</option>
                    <option value="NEW">NEW — Nuovo</option>
                    <option value="NEW_OTHER">NEW_OTHER — Nuovo (altro)</option>
                    <option value="USED_EXCELLENT">USED_EXCELLENT — Usato eccellente</option>
                    <option value="USED_VERY_GOOD">USED_VERY_GOOD — Usato molto buono</option>
                    <option value="USED_GOOD">USED_GOOD — Usato buono</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="ean" className="text-xs text-gray-700">
                    EAN / Codice a barre — opzionale
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="ean"
                      name="ean"
                      type="text"
                      placeholder="Es. 8001234567890 — lascia vuoto se non presente"
                      defaultValue={editingArticle?.ean ?? ""}
                      className="h-8 text-sm"
                      data-testid="ebay-ean-input"
                      maxLength={20}
                    />
                    <AISuggestEanButton
                      selectedFile={selectedFile}
                      existingImageUrl={
                        editingArticle?.images_url?.[0] ||
                        (editingArticle?.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${editingArticle.photo_url}` : null)
                      }
                    />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    Se vuoto, eBay riceve <b>"Does not apply"</b> (richiesto da alcune categorie per articoli da collezione senza codice a barre).
                  </p>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="ebay_aspects" className="text-xs text-gray-700">
                      Specifiche eBay (Item Specifics) — una per riga, formato <code>Chiave: Valore</code>
                    </Label>
                    <EbayAspectsHelperButton />
                  </div>
                  <textarea
                    id="ebay_aspects"
                    name="ebay_aspects"
                    rows={4}
                    defaultValue={
                      editingArticle?.ebay_aspects
                        ? Object.entries(editingArticle.ebay_aspects)
                            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
                            .join("\n")
                        : ""
                    }
                    placeholder={"Es. per Pokemon TCG:\nGioco: Pokémon TCG\nLingua: Italiano\nTipo: Bustina"}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                    data-testid="ebay-aspects-textarea"
                  />
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    Necessario per alcune categorie (es. <b>Pokémon pacchetti</b> richiede <b>Gioco</b>, <b>Modellini</b> richiede <b>Scala</b>). Lascia vuoto se la categoria non richiede specifiche aggiuntive.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="ebay_price" className="text-xs text-gray-700">
                    Prezzo eBay (€) — opzionale
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="ebay_price"
                      name="ebay_price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Vuoto = stesso prezzo del sito"
                      defaultValue={editingArticle?.ebay_price ?? ""}
                      className="h-8 text-sm"
                      data-testid="ebay-price-input"
                    />
                    <button
                      type="button"
                      className="h-8 px-3 text-xs bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded whitespace-nowrap"
                      onClick={() => {
                        const sitePrice = parseFloat(document.getElementById('price')?.value || '0');
                        if (sitePrice > 0) {
                          const ebayPrice = (sitePrice * 1.06).toFixed(2);
                          const inp = document.getElementById('ebay_price');
                          if (inp) inp.value = ebayPrice;
                        }
                      }}
                      data-testid="ebay-price-plus6-btn"
                    >
                      +6% fee
                    </button>
                    <button
                      type="button"
                      className="h-8 px-3 text-xs bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded whitespace-nowrap"
                      onClick={() => {
                        const sitePrice = parseFloat(document.getElementById('price')?.value || '0');
                        if (sitePrice > 0) {
                          const ebayPrice = (sitePrice * 1.10).toFixed(2);
                          const inp = document.getElementById('ebay_price');
                          if (inp) inp.value = ebayPrice;
                        }
                      }}
                      data-testid="ebay-price-plus10-btn"
                    >
                      +10%
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    Lascia vuoto per mantenere lo stesso prezzo del sito. Clicca <b>+6% fee</b> per coprire la commissione eBay, o <b>+10%</b> per un margine extra.
                  </p>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Social (FB + IG) auto-publish toggle — only on create */}
          {!editingArticle && (
            <div className="space-y-3 bg-orange-50 p-4 rounded-lg border border-orange-200" data-testid="social-publish-section">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="publishToSocial"
                  name="publishToSocial"
                  defaultChecked={true}
                  className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  data-testid="social-publish-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="publishToSocial" className="text-sm font-medium text-gray-900 cursor-pointer">
                    🔥 Pubblica anche su Facebook + Instagram <span className="text-green-700 font-semibold">(attivo)</span>
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Ogni nuovo arrivo viene pubblicato automaticamente sulla Pagina FB e su Instagram
                    (foto principale + titolo + prezzo + link). Deseleziona se NON vuoi pubblicare questo articolo.
                    Richiede connessione attiva nel pannello Social.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Legacy eBay edit section - removed (now consolidated above) */}
          {false && editingArticle && editingArticle.ebay && editingArticle.ebay.offer_id && (
            <div className="space-y-3 bg-amber-50 p-4 rounded-lg border border-amber-200" data-testid="ebay-edit-section">
              <h4 className="text-sm font-medium text-gray-900">🟦 Prezzo eBay (listing già attivo)</h4>
              <p className="text-xs text-gray-600">
                Offer ID: <code className="text-amber-700">{editingArticle.ebay.offer_id}</code> · Status: <b>{editingArticle.ebay.status}</b>
              </p>
              <div>
                <Label htmlFor="ebay_price" className="text-xs text-gray-700">
                  Prezzo eBay (€)
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="ebay_price"
                    name="ebay_price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Vuoto = stesso prezzo del sito"
                    defaultValue={editingArticle?.ebay_price ?? ""}
                    className="h-8 text-sm"
                    data-testid="ebay-price-edit-input"
                  />
                  <button
                    type="button"
                    className="h-8 px-3 text-xs bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded whitespace-nowrap"
                    onClick={() => {
                      const sitePrice = parseFloat(document.getElementById('price')?.value || '0');
                      if (sitePrice > 0) {
                        document.getElementById('ebay_price').value = (sitePrice * 1.06).toFixed(2);
                      }
                    }}
                  >
                    +6% fee
                  </button>
                  <button
                    type="button"
                    className="h-8 px-3 text-xs bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded whitespace-nowrap"
                    onClick={() => {
                      const sitePrice = parseFloat(document.getElementById('price')?.value || '0');
                      if (sitePrice > 0) {
                        document.getElementById('ebay_price').value = (sitePrice * 1.10).toFixed(2);
                      }
                    }}
                  >
                    +10%
                  </button>
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  Salvando l'articolo, il listing eBay verrà aggiornato automaticamente con il nuovo prezzo.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              data-testid="article-form-cancel"
              disabled={uploading}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={uploading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="article-form-submit"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {editingArticle ? "Aggiornando..." : "Creando..."}
                </>
              ) : editingArticle ? (
                "Aggiorna Articolo"
              ) : (
                "Crea Articolo"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
